
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Lock, Mail } from 'lucide-react';

interface PasswordChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  currentEmail?: string;
}

export function PasswordChangeDialog({ open, onOpenChange, employeeId, currentEmail }: PasswordChangeDialogProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    email: currentEmail || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // التحقق من تطابق كلمة المرور الجديدة
      if (formData.newPassword !== formData.confirmPassword) {
        toast({
          title: "خطأ",
          description: "كلمة المرور الجديدة غير متطابقة",
          variant: "destructive",
        });
        return;
      }

      // التحقق من طول كلمة المرور
      if (formData.newPassword.length < 6) {
        toast({
          title: "خطأ",
          description: "يجب أن تكون كلمة المرور 6 أحرف على الأقل",
          variant: "destructive",
        });
        return;
      }

      // تحديث كلمة المرور في Supabase Auth
      const { error: passwordError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (passwordError) {
        throw passwordError;
      }

      // تحديث الإيميل في جدول الموظفين
      if (formData.email !== currentEmail) {
        const { error: emailError } = await supabase
          .from('employees')
          .update({ email: formData.email })
          .eq('id', employeeId);

        if (emailError) {
          throw emailError;
        }

        // تحديث الإيميل في Supabase Auth إذا لزم الأمر
        const { error: authEmailError } = await supabase.auth.updateUser({
          email: formData.email
        });

        if (authEmailError) {
          console.warn('Could not update auth email:', authEmailError);
          // لا نرمي خطأ هنا لأن تحديث الإيميل في Auth قد يتطلب تأكيد
        }
      }

      toast({
        title: "تم التحديث بنجاح",
        description: "تم تحديث كلمة المرور بنجاح",
      });

      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        email: formData.email
      });

      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating password/email:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ أثناء التحديث",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            تغيير كلمة المرور والإيميل
          </DialogTitle>
          <DialogDescription>
            قم بتحديث كلمة المرور والإيميل الخاص بك
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">الإيميل الجديد</Label>
            <div className="relative">
              <Mail className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="pr-10"
                placeholder="أدخل الإيميل الجديد"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="currentPassword">كلمة المرور الحالية</Label>
            <div className="relative">
              <Lock className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="pr-10"
                placeholder="أدخل كلمة المرور الحالية"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">كلمة المرور الجديدة</Label>
            <div className="relative">
              <Lock className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                className="pr-10"
                placeholder="أدخل كلمة المرور الجديدة"
                required
                minLength={6}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">تأكيد كلمة المرور الجديدة</Label>
            <div className="relative">
              <Lock className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="pr-10"
                placeholder="أعد إدخال كلمة المرور الجديدة"
                required
                minLength={6}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'جاري التحديث...' : 'تحديث'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
